/**
 * Admin — Platform Settings
 *
 * Configurable platform-wide settings stored as key/value pairs
 * via the SystemSettings API. English only (admin UI).
 *
 * Current settings:
 *   - Project stepper intro video URL
 *
 * Future candidates:
 *   - Default reporting cadence
 *   - Trust score thresholds
 *   - Platform announcement banner text
 *   - Maintenance mode toggle
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getSystemSetting,
  putSystemSetting,
  SETTING_KEYS,
} from '../../api/systemSettings';
import {
  ALL_LANGUAGE_CODES,
  type LanguageCode,
  saveEnabledLanguages,
  invalidateEnabledLanguagesCache,
} from '../../i18n/useEnabledLanguages';
import AdminProjectTaxonomy from './AdminProjectTaxonomy';
import styles from './AdminPages.module.css';

/* ------------------------------------------------------------------ */
/*  Setting field descriptor                                            */
/* ------------------------------------------------------------------ */

interface SettingField {
  key: string;
  label: string;
  hint: string;
  placeholder: string;
  type?: 'text' | 'url';
}

/** Language metadata for admin toggle UI (English-only admin surface). */
const LANGUAGE_META: Record<LanguageCode, { flag: string; label: string }> = {
  en: { flag: '\uD83C\uDDFA\uD83C\uDDF8', label: 'English' },
  ar: { flag: '\uD83C\uDDF8\uD83C\uDDE6', label: 'Arabic' },
  fr: { flag: '\uD83C\uDDEB\uD83C\uDDF7', label: 'French' },
  de: { flag: '\uD83C\uDDE9\uD83C\uDDEA', label: 'German' },
  uk: { flag: '\uD83C\uDDFA\uD83C\uDDE6', label: 'Ukrainian' },
  sv: { flag: '\uD83C\uDDF8\uD83C\uDDEA', label: 'Swedish' },
  ru: { flag: '\uD83C\uDDF7\uD83C\uDDFA', label: 'Russian' },
  es: { flag: '\uD83C\uDDEA\uD83C\uDDF8', label: 'Spanish' },
};

const FIELDS: SettingField[] = [
  {
    key: SETTING_KEYS.STEPPER_VIDEO_URL,
    label: 'Project Stepper — Intro Video URL',
    hint: 'YouTube embed URL or direct .mp4 link. Leave blank to hide the video.',
    placeholder: 'https://www.youtube-nocookie.com/embed/...',
    type: 'url',
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

export default function AdminPlatformSettings() {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const statusTimer = useRef<ReturnType<typeof setTimeout>>();

  // ── Language toggle state ─────────────────────────────────
  const [enabledLangs, setEnabledLangs] = useState<Set<LanguageCode>>(new Set(['en']));
  const [savedLangs, setSavedLangs] = useState<Set<LanguageCode>>(new Set(['en']));
  const [savingLangs, setSavingLangs] = useState(false);

  // ── Load current values ────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result: Record<string, string> = {};
      for (const f of FIELDS) {
        try {
          const val = await getSystemSetting(f.key);
          if (val !== null) result[f.key] = val;
        } catch {
          /* first time — key doesn't exist yet, leave blank */
        }
      }
      // Load enabled languages
      let langs: Set<LanguageCode> = new Set(['en']);
      try {
        const raw = await getSystemSetting(SETTING_KEYS.ENABLED_LANGUAGES);
        if (raw) {
          const codes = raw.split(',').map((s) => s.trim().toLowerCase())
            .filter((c): c is LanguageCode => (ALL_LANGUAGE_CODES as readonly string[]).includes(c));
          if (!codes.includes('en')) codes.unshift('en');
          langs = new Set(codes);
        }
      } catch {
        /* default to English only */
      }
      if (!cancelled) {
        setValues(result);
        setSaved(result);
        setEnabledLangs(langs);
        setSavedLangs(new Set(langs));
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Flash status ───────────────────────────────────────────
  const flash = useCallback((type: 'success' | 'error', msg: string) => {
    setStatus({ type, msg });
    if (statusTimer.current) clearTimeout(statusTimer.current);
    statusTimer.current = setTimeout(() => setStatus(null), 4000);
  }, []);

  // ── Save a single field ────────────────────────────────────
  const handleSave = useCallback(async (key: string) => {
    const val = values[key] ?? '';
    setSaving(key);
    try {
      await putSystemSetting(key, val);
      setSaved((prev) => ({ ...prev, [key]: val }));
      flash('success', `Saved "${key}"`);
    } catch (err: any) {
      flash('error', `Failed to save: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(null);
    }
  }, [values, flash]);

  // ── Save all dirty fields ──────────────────────────────────
  const dirtyKeys = FIELDS.filter((f) => (values[f.key] ?? '') !== (saved[f.key] ?? '')).map((f) => f.key);

  const handleSaveAll = useCallback(async () => {
    for (const key of dirtyKeys) {
      await handleSave(key);
    }
  }, [dirtyKeys, handleSave]);

  // ── Language toggle helpers ────────────────────────────────
  const langsDirty = (() => {
    if (enabledLangs.size !== savedLangs.size) return true;
    for (const code of enabledLangs) {
      if (!savedLangs.has(code)) return true;
    }
    return false;
  })();

  const handleToggleLang = useCallback((code: LanguageCode) => {
    if (code === 'en') return; // English cannot be disabled
    setEnabledLangs((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  const handleSaveLangs = useCallback(async () => {
    setSavingLangs(true);
    try {
      await saveEnabledLanguages(Array.from(enabledLangs));
      setSavedLangs(new Set(enabledLangs));
      invalidateEnabledLanguagesCache();
      flash('success', 'Language settings saved');
    } catch (err: any) {
      flash('error', `Failed to save languages: ${err.message || 'Unknown error'}`);
    } finally {
      setSavingLangs(false);
    }
  }, [enabledLangs, flash]);

  // ── Render ─────────────────────────────────────────────────
  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Platform Settings</h1>
          <p className={styles.pageSubtitle}>
            Configure platform-wide settings. Changes take effect immediately.
          </p>
        </div>
        <div className={styles.pageActions}>
          <button
            className={`${styles.btnPrimary} ${dirtyKeys.length > 0 ? styles.btnDirty : ''}`}
            disabled={dirtyKeys.length === 0 || saving !== null}
            onClick={handleSaveAll}
          >
            {saving ? 'Saving…' : dirtyKeys.length > 0 ? `Save ${dirtyKeys.length} Change${dirtyKeys.length > 1 ? 's' : ''}` : 'All Saved'}
          </button>
        </div>
      </div>

      {status && (
        <div className={`${styles.statusBar} ${status.type === 'success' ? styles.status_success : styles.status_error}`}>
          {status.msg}
        </div>
      )}

      {loading ? (
        <div className={styles.section} style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
          Loading settings…
        </div>
      ) : (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Content &amp; Media</h2>
          </div>
          <p className={styles.sectionHint}>
            URLs and media references used across the platform UI.
          </p>

          {FIELDS.map((field) => {
            const isDirty = (values[field.key] ?? '') !== (saved[field.key] ?? '');
            return (
              <div key={field.key} style={{ marginBottom: 20 }}>
                <label className={styles.fieldLabel}>
                  {field.label}
                  <span className={styles.fieldHint}>{field.hint}</span>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <input
                      className={styles.input}
                      type={field.type || 'text'}
                      value={values[field.key] ?? ''}
                      onChange={(e) => setValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      placeholder={field.placeholder}
                    />
                    <button
                      className={styles.btnSecondary}
                      disabled={!isDirty || saving === field.key}
                      onClick={() => handleSave(field.key)}
                      style={{ flexShrink: 0 }}
                    >
                      {saving === field.key ? 'Saving…' : isDirty ? 'Save' : 'Saved'}
                    </button>
                  </div>
                </label>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Language Settings ───────────────────────────────── */}
      {!loading && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Language Availability</h2>
            <button
              className={`${styles.btnPrimary} ${langsDirty ? styles.btnDirty : ''}`}
              disabled={!langsDirty || savingLangs}
              onClick={handleSaveLangs}
            >
              {savingLangs ? 'Saving\u2026' : langsDirty ? 'Save Languages' : 'All Saved'}
            </button>
          </div>
          <p className={styles.sectionHint}>
            Control which languages appear in the user language picker. English is always enabled.
            Disabled languages won't appear as options for users. If a user's current language is
            disabled, they will be switched to English automatically.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
            {ALL_LANGUAGE_CODES.map((code) => {
              const meta = LANGUAGE_META[code];
              const isEnabled = enabledLangs.has(code);
              const isEnglish = code === 'en';
              return (
                <label
                  key={code}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 14px',
                    borderRadius: 6,
                    border: `1px solid ${isEnabled ? 'rgba(69, 187, 71, 0.3)' : '#e5e7eb'}`,
                    background: isEnabled ? 'rgba(69, 187, 71, 0.04)' : '#fafafa',
                    cursor: isEnglish ? 'default' : 'pointer',
                    opacity: isEnglish ? 0.85 : 1,
                    transition: 'all 0.15s ease',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    disabled={isEnglish}
                    onChange={() => handleToggleLang(code)}
                    style={{ width: 16, height: 16, accentColor: '#45BB47', cursor: isEnglish ? 'default' : 'pointer' }}
                  />
                  <span style={{ fontSize: 20, lineHeight: 1 }}>{meta.flag}</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#1a1a2e' }}>
                    {meta.label}
                    <span style={{ fontSize: 11, color: '#9ca3af', marginInlineStart: 6 }}>({code})</span>
                  </span>
                  {isEnglish && (
                    <span style={{ fontSize: 10, color: '#9ca3af', marginInlineStart: 'auto', fontStyle: 'italic' }}>
                      always on
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Project Taxonomy ─────────────────────────────────── */}
      <AdminProjectTaxonomy />
    </div>
  );
}
